import { PRODUCT_NAME, passkeysSupported } from "@staffsmith/web-auth";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "../context.js";
import { QrCode } from "../qr.js";
import { ErrorNote } from "../ui.js";

// Operators sign in with a password and an authenticator app's code, always
// (QUESTIONS.md Q49), or with a passkey added after that. The first sign-in sets
// the authenticator up: the app shows a QR code (and the secret it carries), and
// the first code from it finishes signing in.

type Mode =
  | { kind: "password" }
  | { kind: "code"; email: string; session: string }
  | { kind: "setup"; email: string; session: string; secret: string; uri: string };

export function SignInPage() {
  const { auth } = useServices();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>({ kind: "password" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const attempt = async (step: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const codeInput = (
    <label>
      Code from your authenticator app
      <input
        name="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        required
      />
    </label>
  );

  return (
    <main className="sign-in">
      <h1>{PRODUCT_NAME} operators</h1>
      <p className="muted">Platform operators only. Every action here is recorded.</p>
      {mode.kind === "password" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const email = String(form.get("email"));
            void attempt(async () => {
              const next = await auth.startPasswordWithAuthenticator(
                email,
                String(form.get("password")),
              );
              setMode({ ...next, email });
            });
          }}
        >
          <label>
            Email
            <input name="email" type="email" autoComplete="username" required />
          </label>
          <label>
            Password
            <input name="password" type="password" autoComplete="current-password" required />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Checking…" : "Continue"}
          </button>
          {passkeysSupported() ? (
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={(e) => {
                const email = e.currentTarget.form?.elements.namedItem("email");
                const value = email instanceof HTMLInputElement ? email.value.trim() : "";
                if (!value) {
                  setError(new Error("Enter your email first."));
                  return;
                }
                void attempt(async () => {
                  await auth.signInWithPasskey(value);
                  void navigate("/");
                });
              }}
            >
              Sign in with a passkey
            </button>
          ) : null}
        </form>
      ) : mode.kind === "code" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const code = String(new FormData(e.currentTarget).get("code")).trim();
            void attempt(async () => {
              await auth.confirmAuthenticatorCode(mode.email, mode.session, code);
              void navigate("/");
            });
          }}
        >
          {codeInput}
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" className="link" onClick={() => setMode({ kind: "password" })}>
            Start again
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const code = String(new FormData(e.currentTarget).get("code")).trim();
            void attempt(async () => {
              await auth.finishAuthenticatorSetup(mode.email, mode.session, code);
              void navigate("/");
            });
          }}
        >
          <p>
            Set up your authenticator app (such as Microsoft Authenticator or Google Authenticator):
            add an account and scan this code.
          </p>
          <QrCode text={mode.uri} label="QR code for your authenticator app" />
          <p className="muted">
            Can't scan it? Enter this key instead: <code className="secret">{mode.secret}</code>.
            Or, on a phone with the app installed, <a href={mode.uri}>open this link</a>.
          </p>
          {codeInput}
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Finishing…" : "Finish set-up and sign in"}
          </button>
        </form>
      )}
    </main>
  );
}
